import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import EmailModal, { isValidEmail } from 'pages/BankSlip/components/EmailModal';
import BankSlipViewModal from 'pages/BankSlip/components/BankSlipViewModal';
import {
  emailBankSlipsBulk,
  runBankSlipAction,
} from 'pages/BankSlip/utils/bankSlipActions';
import {
  BankSlipRow,
  formatInstallmentAmount,
} from 'pages/BankSlip/utils/bankSlipDisplay';
import {
  showBankSlipDownloadSuccessToast,
  showBankSlipEmailSuccessToast,
} from 'pages/BankSlip/utils/bankSlipToast';

export type UseBankSlipRowActionsOptions = {
  showMobileMode?: boolean;
  onViewClose?: () => void;
  onEmailSent?: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

export type UseBankSlipRowActions = {
  download: (row: BankSlipRow) => Promise<void>;
  openEmail: (rows: BankSlipRow | BankSlipRow[]) => void;
  openView: (row: BankSlipRow) => void;
  emailOpen: boolean;
  viewOpen: boolean;
  modals: JSX.Element;
};

const sumAmount = (rows: BankSlipRow[]): string => {
  if (rows.length === 1) {
    return formatInstallmentAmount(rows[0].installment, rows[0].invoice);
  }

  const total = rows.reduce((sum, row) => {
    const amount =
      row.installment.amountOriginal ?? row.invoice.totalAmount ?? 0;
    return sum + Number(amount);
  }, 0);

  return total.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const useBankSlipRowActions = ({
  showMobileMode = false,
  onViewClose,
  onEmailSent,
  onLoadingChange,
}: UseBankSlipRowActionsOptions = {}): UseBankSlipRowActions => {
  const [emailRows, setEmailRows] = useState<BankSlipRow[]>([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [viewRow, setViewRow] = useState<BankSlipRow | null>(null);

  const mounted = useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const download = useCallback(
    async (row: BankSlipRow): Promise<void> => {
      onLoadingChange?.(true);

      try {
        await runBankSlipAction(row, 'DOWNLOAD');
        if (!mounted.current) return;
        showBankSlipDownloadSuccessToast();
      } catch (error) {
        if (!mounted.current) return;
        toast.error(
          (error as Error)?.message || 'Ocorreu um erro ao baixar o boleto'
        );
      } finally {
        onLoadingChange?.(false);
      }
    },
    [onLoadingChange]
  );

  const openEmail = useCallback((rows: BankSlipRow | BankSlipRow[]): void => {
    const list = Array.isArray(rows) ? rows : [rows];
    if (list.length === 0) return;

    setEmailRows(list);
    setEmailValue(
      localStorage.getItem('@UltragazEmpresarial:loginEmail') ?? ''
    );
    setEmailOpen(true);
  }, []);

  const closeEmail = useCallback((): void => {
    if (emailLoading) return;
    setEmailOpen(false);
    setEmailRows([]);
  }, [emailLoading]);

  const submitEmail = useCallback(async (): Promise<void> => {
    if (!isValidEmail(emailValue) || emailRows.length === 0) return;

    const email = emailValue.trim();
    setEmailLoading(true);

    try {
      if (emailRows.length === 1) {
        await runBankSlipAction(emailRows[0], 'EMAIL', { email });
      } else {
        await emailBankSlipsBulk(
          emailRows.map((row) => ({
            invoiceId: row.invoice.invoiceId,
            installmentId: row.installment.installmentId,
          })),
          email
        );
      }

      if (!mounted.current) return;

      showBankSlipEmailSuccessToast();
      setEmailOpen(false);
      setEmailRows([]);
      onEmailSent?.();
    } catch (error) {
      if (!mounted.current) return;
      toast.error(
        (error as Error)?.message || 'Ocorreu um erro ao enviar o boleto'
      );
    } finally {
      if (mounted.current) setEmailLoading(false);
    }
  }, [emailRows, emailValue, onEmailSent]);

  const openView = useCallback((row: BankSlipRow): void => {
    setViewRow(row);
  }, []);

  const closeView = useCallback((): void => {
    setViewRow(null);
    onViewClose?.();
  }, [onViewClose]);

  const modals = useMemo(
    () => (
      <>
        <EmailModal
          open={emailOpen}
          email={emailValue}
          boletoCount={emailRows.length}
          totalAmount={sumAmount(emailRows)}
          loading={emailLoading}
          onEmailChange={setEmailValue}
          onClose={closeEmail}
          onSubmit={submitEmail}
        />
        <BankSlipViewModal
          open={Boolean(viewRow)}
          invoice={viewRow?.invoice ?? null}
          installment={viewRow?.installment ?? null}
          showMobileMode={showMobileMode}
          onClose={closeView}
        />
      </>
    ),
    [
      closeEmail,
      closeView,
      emailLoading,
      emailOpen,
      emailRows,
      emailValue,
      showMobileMode,
      submitEmail,
      viewRow,
    ]
  );

  return {
    download,
    openEmail,
    openView,
    emailOpen,
    viewOpen: Boolean(viewRow),
    modals,
  };
};

export default useBankSlipRowActions;
